/**
 * Unified Connection Management System
 * Single source of truth for all user connections and integrations
 * Eliminates duplication between finance/accounts and integrations pages
 */

import { z } from 'zod';
import { EventEmitter } from 'events';
import { PrismaClient } from '@prisma/client';
import { logger } from '@/lib/logger';
import { redisCluster } from '@/lib/cache/RedisCluster';

// Connection schemas
const ConnectionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  type: z.enum(['FINANCIAL_ACCOUNT', 'API_INTEGRATION', 'MARKETPLACE_APP']),
  providerId: z.string(),
  providerName: z.string(),
  connectionType: z.enum(['MANUAL', 'API_OAUTH', 'API_KEY', 'WEBHOOK']),
  status: z.enum(['ACTIVE', 'INACTIVE', 'ERROR', 'PENDING', 'EXPIRED']),
  health: z.enum(['HEALTHY', 'DEGRADED', 'UNHEALTHY']),
  credentials: z.record(z.any()).optional(),
  metadata: z.record(z.any()).optional(),
  permissions: z.array(z.string()),
  lastSynced: z.date().optional(),
  lastHealthCheck: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  expiresAt: z.date().optional()
});

const ConnectionConfigSchema = z.object({
  providerId: z.string(),
  providerName: z.string(),
  type: z.enum(['FINANCIAL_ACCOUNT', 'API_INTEGRATION', 'MARKETPLACE_APP']),
  category: z.string(),
  description: z.string(),
  logo: z.string().optional(),
  isVerified: z.boolean().default(false),
  connectionTypes: z.array(z.enum(['MANUAL', 'API_OAUTH', 'API_KEY', 'WEBHOOK'])),
  requiredPermissions: z.array(z.string()),
  supportedFeatures: z.array(z.string()),
  pricing: z.object({
    type: z.enum(['FREE', 'FREEMIUM', 'PAID']),
    price: z.string().optional(),
    billingCycle: z.string().optional()
  }),
  regions: z.array(z.string()),
  compliance: z.array(z.string()),
  healthCheckConfig: z.object({
    endpoint: z.string().optional(),
    interval: z.number().default(300000), // 5 minutes
    timeout: z.number().default(5000)
  }).optional()
});

type Connection = z.infer<typeof ConnectionSchema>;
type ConnectionConfig = z.infer<typeof ConnectionConfigSchema>;

export interface ConnectionMetrics {
  connectionId: string;
  apiCalls: number;
  errors: number;
  successRate: number;
  averageLatency: number;
  lastActivity: Date;
  dailyUsage: Record<string, number>;
  healthHistory: Array<{
    timestamp: Date;
    status: 'HEALTHY' | 'DEGRADED' | 'UNHEALTHY';
    latency: number;
    error?: string;
  }>;
}

export interface ConnectionFilter {
  userId?: string;
  type?: string;
  status?: string;
  providerId?: string;
  category?: string;
  region?: string;
}

export class UnifiedConnectionManager extends EventEmitter {
  private static instance: UnifiedConnectionManager;
  private prisma: PrismaClient;
  private connectionConfigs: Map<string, ConnectionConfig> = new Map();
  private activeConnections: Map<string, Connection> = new Map();
  private connectionMetrics: Map<string, ConnectionMetrics> = new Map();
  private healthCheckIntervals: Map<string, NodeJS.Timeout> = new Map();
  private isInitialized = false;

  constructor(prisma: PrismaClient) {
    super();
    this.prisma = prisma;
  }

  public static getInstance(prisma: PrismaClient): UnifiedConnectionManager {
    if (!UnifiedConnectionManager.instance) {
      UnifiedConnectionManager.instance = new UnifiedConnectionManager(prisma);
    }
    return UnifiedConnectionManager.instance;
  }

  /**
   * Initialize the connection manager
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      logger.info('Initializing Unified Connection Manager');

      // Load available connection providers
      await this.loadConnectionConfigs();

      // Load active user connections
      await this.loadActiveConnections();

      // Start health monitoring
      this.startHealthMonitoring();

      this.isInitialized = true;
      logger.info('Unified Connection Manager initialized successfully');

      this.emit('initialized');
    } catch (error) {
      logger.error('Failed to initialize Unified Connection Manager', { error });
      throw error;
    }
  }

  /**
   * Get available connection providers
   */
  getAvailableProviders(filters: {
    type?: string;
    category?: string;
    region?: string;
    verified?: boolean;
  } = {}): ConnectionConfig[] {
    return Array.from(this.connectionConfigs.values()).filter(config => {
      if (filters.type && config.type !== filters.type) return false;
      if (filters.category && config.category !== filters.category) return false;
      if (filters.region && !config.regions.includes(filters.region)) return false;
      if (filters.verified !== undefined && config.isVerified !== filters.verified) return false;
      return true;
    });
  }

  /**
   * Create a new connection
   */
  async createConnection(
    userId: string,
    providerId: string,
    connectionData: {
      type: 'MANUAL' | 'API_OAUTH' | 'API_KEY' | 'WEBHOOK';
      credentials?: Record<string, any>;
      metadata?: Record<string, any>;
      permissions: string[];
      expiresAt?: Date;
    }
  ): Promise<Connection> {
    const config = this.connectionConfigs.get(providerId);
    if (!config) {
      throw new Error(`Provider ${providerId} not found`);
    }

    try {
      const connection: Connection = {
        id: crypto.randomUUID(),
        userId,
        type: config.type,
        providerId,
        providerName: config.providerName,
        connectionType: connectionData.type,
        status: 'PENDING',
        health: 'HEALTHY',
        credentials: connectionData.credentials,
        metadata: connectionData.metadata || {},
        permissions: connectionData.permissions,
        createdAt: new Date(),
        updatedAt: new Date(),
        expiresAt: connectionData.expiresAt
      };

      // Validate connection
      ConnectionSchema.parse(connection);

      // Store in database
      await this.persistConnection(connection);

      // Cache in memory
      this.activeConnections.set(connection.id, connection);

      // Initialize metrics
      this.initializeConnectionMetrics(connection.id);

      // Start health monitoring if applicable
      if (config.healthCheckConfig) {
        this.startConnectionHealthCheck(connection.id);
      }

      // Attempt to activate the connection
      await this.activateConnection(connection.id);

      logger.info('Connection created successfully', {
        connectionId: connection.id,
        userId,
        providerId,
        type: config.type
      });

      this.emit('connectionCreated', connection);
      return connection;
    } catch (error) {
      logger.error('Failed to create connection', { userId, providerId, error });
      throw error;
    }
  }

  /**
   * Get user connections
   */
  async getUserConnections(userId: string, filters: ConnectionFilter = {}): Promise<Connection[]> {
    try {
      // Build filter criteria
      const filterCriteria = { userId, ...filters };
      
      // Get from cache if available
      const cacheKey = `connections:${userId}:${JSON.stringify(filterCriteria)}`;
      const cached = await redisCluster.get<Connection[]>(cacheKey);
      if (cached) {
        return cached;
      }

      // Query database
      const connections = Array.from(this.activeConnections.values()).filter(conn => {
        if (conn.userId !== userId) return false;
        if (filters.type && conn.type !== filters.type) return false;
        if (filters.status && conn.status !== filters.status) return false;
        if (filters.providerId && conn.providerId !== filters.providerId) return false;
        return true;
      });

      // Cache result
      await redisCluster.set(cacheKey, connections, 300, ['connections', userId]); // 5 minutes

      return connections;
    } catch (error) {
      logger.error('Failed to get user connections', { userId, error });
      throw error;
    }
  }

  /**
   * Get connection details
   */
  async getConnection(connectionId: string, userId?: string): Promise<Connection | null> {
    const connection = this.activeConnections.get(connectionId);
    
    if (!connection) {
      return null;
    }

    // Verify user ownership if userId provided
    if (userId && connection.userId !== userId) {
      throw new Error('Connection not found or access denied');
    }

    return connection;
  }

  /**
   * Update connection
   */
  async updateConnection(
    connectionId: string,
    userId: string,
    updates: Partial<{
      status: string;
      credentials: Record<string, any>;
      metadata: Record<string, any>;
      permissions: string[];
      expiresAt: Date;
    }>
  ): Promise<Connection> {
    const connection = await this.getConnection(connectionId, userId);
    if (!connection) {
      throw new Error('Connection not found');
    }

    try {
      const updatedConnection = {
        ...connection,
        ...updates,
        updatedAt: new Date()
      };

      // Validate updated connection
      ConnectionSchema.parse(updatedConnection);

      // Update in database
      await this.persistConnection(updatedConnection);

      // Update in cache
      this.activeConnections.set(connectionId, updatedConnection);

      logger.info('Connection updated successfully', { connectionId, userId });

      this.emit('connectionUpdated', updatedConnection);
      return updatedConnection;
    } catch (error) {
      logger.error('Failed to update connection', { connectionId, userId, error });
      throw error;
    }
  }

  /**
   * Delete connection
   */
  async deleteConnection(connectionId: string, userId: string): Promise<void> {
    const connection = await this.getConnection(connectionId, userId);
    if (!connection) {
      throw new Error('Connection not found');
    }

    try {
      // Stop health monitoring
      this.stopConnectionHealthCheck(connectionId);

      // Remove from database
      await this.removeConnectionFromDatabase(connectionId);

      // Remove from cache
      this.activeConnections.delete(connectionId);
      this.connectionMetrics.delete(connectionId);

      // Clear cached data
      await redisCluster.invalidateByTags(['connections', userId, connectionId]);

      logger.info('Connection deleted successfully', { connectionId, userId });

      this.emit('connectionDeleted', { connectionId, userId });
    } catch (error) {
      logger.error('Failed to delete connection', { connectionId, userId, error });
      throw error;
    }
  }

  /**
   * Get connection metrics
   */
  async getConnectionMetrics(connectionId: string, userId?: string): Promise<ConnectionMetrics | null> {
    const connection = await this.getConnection(connectionId, userId);
    if (!connection) {
      return null;
    }

    return this.connectionMetrics.get(connectionId) || null;
  }

  /**
   * Test connection health
   */
  async testConnection(connectionId: string, userId?: string): Promise<{
    healthy: boolean;
    latency: number;
    error?: string;
    timestamp: Date;
  }> {
    const connection = await this.getConnection(connectionId, userId);
    if (!connection) {
      throw new Error('Connection not found');
    }

    const config = this.connectionConfigs.get(connection.providerId);
    if (!config?.healthCheckConfig?.endpoint) {
      return {
        healthy: true,
        latency: 0,
        timestamp: new Date(),
        error: 'No health check configured'
      };
    }

    const startTime = Date.now();
    
    try {
      const response = await fetch(config.healthCheckConfig.endpoint, {
        method: 'GET',
        timeout: config.healthCheckConfig.timeout,
        headers: this.buildAuthHeaders(connection)
      });

      const latency = Date.now() - startTime;
      const healthy = response.ok;

      // Update health status
      const healthStatus = healthy ? 'HEALTHY' : 'DEGRADED';
      await this.updateConnectionHealth(connectionId, healthStatus, latency);

      return {
        healthy,
        latency,
        timestamp: new Date(),
        error: healthy ? undefined : `HTTP ${response.status}`
      };
    } catch (error) {
      const latency = Date.now() - startTime;
      await this.updateConnectionHealth(connectionId, 'UNHEALTHY', latency, error as Error);

      return {
        healthy: false,
        latency,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Record API usage
   */
  async recordAPIUsage(
    connectionId: string,
    operation: string,
    success: boolean,
    latency: number,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const metrics = this.connectionMetrics.get(connectionId);
      if (!metrics) return;

      // Update metrics
      metrics.apiCalls++;
      if (!success) metrics.errors++;
      metrics.averageLatency = (metrics.averageLatency + latency) / 2;
      metrics.successRate = ((metrics.apiCalls - metrics.errors) / metrics.apiCalls) * 100;
      metrics.lastActivity = new Date();

      // Update daily usage
      const today = new Date().toISOString().split('T')[0];
      metrics.dailyUsage[today] = (metrics.dailyUsage[today] || 0) + 1;

      // Cache metrics
      await redisCluster.set(
        `connection:metrics:${connectionId}`,
        metrics,
        3600, // 1 hour
        ['connections', 'metrics', connectionId]
      );

      this.emit('apiUsageRecorded', { connectionId, operation, success, latency, metadata });
    } catch (error) {
      logger.error('Failed to record API usage', { connectionId, error });
    }
  }

  /**
   * Get unified dashboard data
   */
  async getDashboardData(userId: string): Promise<{
    totalConnections: number;
    activeConnections: number;
    healthyConnections: number;
    errorConnections: number;
    recentActivity: Array<{
      connectionId: string;
      providerName: string;
      activity: string;
      timestamp: Date;
    }>;
    connectionsByType: Record<string, number>;
    connectionsByStatus: Record<string, number>;
  }> {
    try {
      const connections = await this.getUserConnections(userId);
      
      const totalConnections = connections.length;
      const activeConnections = connections.filter(c => c.status === 'ACTIVE').length;
      const healthyConnections = connections.filter(c => c.health === 'HEALTHY').length;
      const errorConnections = connections.filter(c => c.status === 'ERROR').length;

      // Get recent activity
      const recentActivity = connections
        .map(conn => {
          const metrics = this.connectionMetrics.get(conn.id);
          return {
            connectionId: conn.id,
            providerName: conn.providerName,
            activity: 'API Call',
            timestamp: metrics?.lastActivity || conn.updatedAt
          };
        })
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 10);

      // Group by type and status
      const connectionsByType = connections.reduce((acc, conn) => {
        acc[conn.type] = (acc[conn.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const connectionsByStatus = connections.reduce((acc, conn) => {
        acc[conn.status] = (acc[conn.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return {
        totalConnections,
        activeConnections,
        healthyConnections,
        errorConnections,
        recentActivity,
        connectionsByType,
        connectionsByStatus
      };
    } catch (error) {
      logger.error('Failed to get dashboard data', { userId, error });
      throw error;
    }
  }

  /**
   * Load connection configurations
   */
  private async loadConnectionConfigs(): Promise<void> {
    // In a real implementation, this would load from database or configuration files
    // For now, we'll define the core providers inline
    const configs: ConnectionConfig[] = [
      // Financial Account Providers - Global Banking APIs
      {
        providerId: 'plaid_us',
        providerName: 'Plaid (USA)',
        type: 'FINANCIAL_ACCOUNT',
        category: 'Banking',
        description: 'Connect US bank accounts via Plaid',
        logo: '/providers/plaid.png',
        isVerified: true,
        connectionTypes: ['API_OAUTH'],
        requiredPermissions: ['accounts', 'transactions'],
        supportedFeatures: ['account_info', 'transaction_history', 'balance_inquiry'],
        pricing: { type: 'FREEMIUM', price: '$0.30 per API call' },
        regions: ['US'],
        compliance: ['PCI_DSS', 'CCPA'],
        healthCheckConfig: {
          endpoint: 'https://production.plaid.com/health',
          interval: 300000,
          timeout: 5000
        }
      },
      {
        providerId: 'plaid_uk',
        providerName: 'Plaid (UK)',
        type: 'FINANCIAL_ACCOUNT',
        category: 'Banking',
        description: 'Connect UK bank accounts via Plaid',
        logo: '/providers/plaid.png',
        isVerified: true,
        connectionTypes: ['API_OAUTH'],
        requiredPermissions: ['accounts', 'transactions'],
        supportedFeatures: ['account_info', 'transaction_history', 'balance_inquiry'],
        pricing: { type: 'FREEMIUM', price: '$0.30 per API call' },
        regions: ['UK'],
        compliance: ['PSD2', 'GDPR'],
        healthCheckConfig: {
          endpoint: 'https://production.plaid.com/health',
          interval: 300000,
          timeout: 5000
        }
      },
      {
        providerId: 'truelayer_uk',
        providerName: 'TrueLayer (UK)',
        type: 'FINANCIAL_ACCOUNT',
        category: 'Banking',
        description: 'Connect UK bank accounts via TrueLayer Open Banking',
        logo: '/providers/truelayer.png',
        isVerified: true,
        connectionTypes: ['API_OAUTH'],
        requiredPermissions: ['accounts', 'transactions'],
        supportedFeatures: ['account_info', 'transaction_history', 'balance_inquiry'],
        pricing: { type: 'FREEMIUM', price: 'Free tier available' },
        regions: ['UK'],
        compliance: ['PSD2', 'GDPR'],
        healthCheckConfig: {
          endpoint: 'https://api.truelayer.com/v1/health',
          interval: 300000,
          timeout: 5000
        }
      },
      {
        providerId: 'truelayer_eu',
        providerName: 'TrueLayer (Europe)',
        type: 'FINANCIAL_ACCOUNT',
        category: 'Banking',
        description: 'Connect European bank accounts via TrueLayer',
        logo: '/providers/truelayer.png',
        isVerified: true,
        connectionTypes: ['API_OAUTH'],
        requiredPermissions: ['accounts', 'transactions'],
        supportedFeatures: ['account_info', 'transaction_history', 'balance_inquiry'],
        pricing: { type: 'FREEMIUM', price: 'Free tier available' },
        regions: ['EU'],
        compliance: ['PSD2', 'GDPR'],
        healthCheckConfig: {
          endpoint: 'https://api.truelayer.com/v1/health',
          interval: 300000,
          timeout: 5000
        }
      },
      {
        providerId: 'saltedge_eu',
        providerName: 'Salt Edge (Europe)',
        type: 'FINANCIAL_ACCOUNT',
        category: 'Banking',
        description: 'Connect European bank accounts via Salt Edge',
        logo: '/providers/saltedge.png',
        isVerified: true,
        connectionTypes: ['API_OAUTH'],
        requiredPermissions: ['accounts', 'transactions'],
        supportedFeatures: ['account_info', 'transaction_history', 'balance_inquiry'],
        pricing: { type: 'FREEMIUM', price: 'Free tier available' },
        regions: ['EU'],
        compliance: ['PSD2', 'GDPR'],
        healthCheckConfig: {
          endpoint: 'https://www.saltedge.com/api/v5/health',
          interval: 300000,
          timeout: 5000
        }
      },
      {
        providerId: 'basiq_au',
        providerName: 'Basiq (Australia)',
        type: 'FINANCIAL_ACCOUNT',
        category: 'Banking',
        description: 'Connect Australian bank accounts via Basiq CDR',
        logo: '/providers/basiq.png',
        isVerified: true,
        connectionTypes: ['API_OAUTH'],
        requiredPermissions: ['accounts', 'transactions'],
        supportedFeatures: ['account_info', 'transaction_history', 'balance_inquiry'],
        pricing: { type: 'FREEMIUM', price: 'Free tier available' },
        regions: ['AU'],
        compliance: ['CDR', 'PCI_DSS'],
        healthCheckConfig: {
          endpoint: 'https://au-api.basiq.io/health',
          interval: 300000,
          timeout: 5000
        }
      },
      {
        providerId: 'finvu_in',
        providerName: 'Finvu (India)',
        type: 'FINANCIAL_ACCOUNT',
        category: 'Banking',
        description: 'Connect Indian bank accounts via Finvu AA Framework',
        logo: '/providers/finvu.png',
        isVerified: true,
        connectionTypes: ['API_OAUTH'],
        requiredPermissions: ['accounts', 'transactions'],
        supportedFeatures: ['account_info', 'transaction_history', 'balance_inquiry'],
        pricing: { type: 'FREEMIUM', price: 'Free tier available' },
        regions: ['IN'],
        compliance: ['AA_Framework', 'DPDPA'],
        healthCheckConfig: {
          endpoint: 'https://api.finvu.in/health',
          interval: 300000,
          timeout: 5000
        }
      },
      {
        providerId: 'mastercard_au',
        providerName: 'Mastercard Open Banking (Australia)',
        type: 'FINANCIAL_ACCOUNT',
        category: 'Banking',
        description: 'Connect Australian bank accounts via Mastercard',
        logo: '/providers/mastercard.png',
        isVerified: true,
        connectionTypes: ['API_OAUTH', 'MANUAL'],
        requiredPermissions: ['accounts', 'transactions'],
        supportedFeatures: ['account_info', 'transaction_history'],
        pricing: { type: 'FREE' },
        regions: ['AU'],
        compliance: ['CDR', 'PCI_DSS']
      },
      // Marketplace Integrations
      {
        providerId: 'quickbooks',
        providerName: 'QuickBooks',
        type: 'MARKETPLACE_APP',
        category: 'Accounting',
        description: 'Sync financial data with QuickBooks',
        logo: '/providers/quickbooks.png',
        isVerified: true,
        connectionTypes: ['API_OAUTH'],
        requiredPermissions: ['accounting.read', 'accounting.write'],
        supportedFeatures: ['sync_transactions', 'export_reports'],
        pricing: { type: 'PAID', price: '$9.99/month' },
        regions: ['US', 'CA', 'UK', 'AU'],
        compliance: ['SOC2']
      },
      {
        providerId: 'stripe',
        providerName: 'Stripe',
        type: 'API_INTEGRATION',
        category: 'Payment Processing',
        description: 'Payment processing and analytics',
        logo: '/providers/stripe.png',
        isVerified: true,
        connectionTypes: ['API_KEY', 'WEBHOOK'],
        requiredPermissions: ['payments.read'],
        supportedFeatures: ['payment_tracking', 'revenue_analytics'],
        pricing: { type: 'FREE' },
        regions: ['US', 'EU', 'UK', 'AU'],
        compliance: ['PCI_DSS']
      }
    ];

    for (const config of configs) {
      this.connectionConfigs.set(config.providerId, config);
    }

    logger.info('Connection configurations loaded', { count: configs.length });
  }

  /**
   * Load active connections from database
   */
  private async loadActiveConnections(): Promise<void> {
    try {
      // In a real implementation, this would load from database
      // For now, we'll initialize empty
      logger.info('Active connections loaded', { count: 0 });
    } catch (error) {
      logger.error('Failed to load active connections', { error });
    }
  }

  /**
   * Initialize connection metrics
   */
  private initializeConnectionMetrics(connectionId: string): void {
    const metrics: ConnectionMetrics = {
      connectionId,
      apiCalls: 0,
      errors: 0,
      successRate: 100,
      averageLatency: 0,
      lastActivity: new Date(),
      dailyUsage: {},
      healthHistory: []
    };

    this.connectionMetrics.set(connectionId, metrics);
  }

  /**
   * Start health monitoring for all connections
   */
  private startHealthMonitoring(): void {
    setInterval(async () => {
      for (const [connectionId, connection] of this.activeConnections) {
        if (connection.status === 'ACTIVE') {
          try {
            await this.testConnection(connectionId);
          } catch (error) {
            logger.error('Health check failed', { connectionId, error });
          }
        }
      }
    }, 60000); // Every minute
  }

  /**
   * Start health check for specific connection
   */
  private startConnectionHealthCheck(connectionId: string): void {
    const config = this.connectionConfigs.get(
      this.activeConnections.get(connectionId)?.providerId || ''
    );

    if (!config?.healthCheckConfig) return;

    const interval = setInterval(async () => {
      try {
        await this.testConnection(connectionId);
      } catch (error) {
        logger.error('Connection health check failed', { connectionId, error });
      }
    }, config.healthCheckConfig.interval);

    this.healthCheckIntervals.set(connectionId, interval);
  }

  /**
   * Stop health check for specific connection
   */
  private stopConnectionHealthCheck(connectionId: string): void {
    const interval = this.healthCheckIntervals.get(connectionId);
    if (interval) {
      clearInterval(interval);
      this.healthCheckIntervals.delete(connectionId);
    }
  }

  /**
   * Update connection health status
   */
  private async updateConnectionHealth(
    connectionId: string,
    health: 'HEALTHY' | 'DEGRADED' | 'UNHEALTHY',
    latency: number,
    error?: Error
  ): Promise<void> {
    const connection = this.activeConnections.get(connectionId);
    if (!connection) return;

    // Update connection health
    connection.health = health;
    connection.lastHealthCheck = new Date();
    connection.updatedAt = new Date();

    // Update metrics
    const metrics = this.connectionMetrics.get(connectionId);
    if (metrics) {
      metrics.healthHistory.push({
        timestamp: new Date(),
        status: health,
        latency,
        error: error?.message
      });

      // Keep only last 100 health checks
      if (metrics.healthHistory.length > 100) {
        metrics.healthHistory = metrics.healthHistory.slice(-100);
      }
    }

    // Persist changes
    await this.persistConnection(connection);

    this.emit('healthUpdated', { connectionId, health, latency, error });
  }

  /**
   * Activate a connection
   */
  private async activateConnection(connectionId: string): Promise<void> {
    const connection = this.activeConnections.get(connectionId);
    if (!connection) return;

    try {
      // Perform provider-specific activation logic
      connection.status = 'ACTIVE';
      connection.lastSynced = new Date();
      connection.updatedAt = new Date();

      await this.persistConnection(connection);

      logger.info('Connection activated', { connectionId });
      this.emit('connectionActivated', connection);
    } catch (error) {
      connection.status = 'ERROR';
      connection.updatedAt = new Date();
      await this.persistConnection(connection);

      logger.error('Failed to activate connection', { connectionId, error });
      this.emit('connectionError', { connectionId, error });
    }
  }

  /**
   * Build authentication headers for API requests
   */
  private buildAuthHeaders(connection: Connection): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'User-Agent': 'PI-APP-Connection-Manager/1.0'
    };

    // Add provider-specific auth headers based on connection type
    switch (connection.connectionType) {
      case 'API_KEY':
        if (connection.credentials?.apiKey) {
          headers['Authorization'] = `Bearer ${connection.credentials.apiKey}`;
        }
        break;
      case 'API_OAUTH':
        if (connection.credentials?.accessToken) {
          headers['Authorization'] = `Bearer ${connection.credentials.accessToken}`;
        }
        break;
    }

    return headers;
  }

  /**
   * Persist connection to database
   */
  private async persistConnection(connection: Connection): Promise<void> {
    try {
      // In a real implementation, this would use Prisma to save to database
      // For now, we'll just update the in-memory cache
      this.activeConnections.set(connection.id, connection);

      // Also cache in Redis
      await redisCluster.set(
        `connection:${connection.id}`,
        connection,
        3600, // 1 hour
        ['connections', connection.userId, connection.id]
      );
    } catch (error) {
      logger.error('Failed to persist connection', { connectionId: connection.id, error });
      throw error;
    }
  }

  /**
   * Remove connection from database
   */
  private async removeConnectionFromDatabase(connectionId: string): Promise<void> {
    try {
      // In a real implementation, this would use Prisma to delete from database
      await redisCluster.delete(`connection:${connectionId}`);
    } catch (error) {
      logger.error('Failed to remove connection from database', { connectionId, error });
      throw error;
    }
  }

  /**
   * Shutdown the connection manager
   */
  async shutdown(): Promise<void> {
    // Stop all health check intervals
    for (const interval of this.healthCheckIntervals.values()) {
      clearInterval(interval);
    }

    this.healthCheckIntervals.clear();
    this.activeConnections.clear();
    this.connectionMetrics.clear();
    this.connectionConfigs.clear();

    this.isInitialized = false;
    logger.info('Unified Connection Manager shutdown complete');
    this.emit('shutdown');
  }
}

// Export singleton factory
export const createUnifiedConnectionManager = (prisma: PrismaClient) =>
  UnifiedConnectionManager.getInstance(prisma);